import React, { useState, useRef, useEffect } from 'react';
import { Search, X, MapPin } from 'lucide-react';

interface SearchBarProps {
  placeholder: string;
  onChange: (value: string) => void;
  popularItems?: { id: number; name: string }[];
  onItemClick?: (id: number) => void;
}

export default function SearchBar({ placeholder, onChange, popularItems, onItemClick }: SearchBarProps) {
  const [value, setValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    onChange(newValue);
  };

  const handleClear = () => {
    setValue('');
    onChange('');
    inputRef.current?.focus();
  };

  const handleItemClick = (id: number, name: string) => {
    if (onItemClick) {
      onItemClick(id);
      setValue(name);
      setIsFocused(false);
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsFocused(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative w-full md:w-auto" ref={dropdownRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          className="w-full min-w-[200px] md:min-w-[300px] bg-[#1a1d24] text-white pl-10 pr-10 py-3 rounded-xl border border-white/10 focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 focus:outline-none transition-all duration-200 placeholder-white/30"
        />
        <Search className="absolute left-3 top-3.5 w-5 h-5 text-white/60" />
        {value && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-3.5 text-white/60 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {isFocused && popularItems && popularItems.length > 0 && !value && (
        <div className="absolute z-50 w-full mt-2 bg-[#24272e] rounded-xl border border-white/10 shadow-xl overflow-hidden">
          <div className="p-3 border-b border-white/10">
            <p className="text-sm text-white/60">Popüler Şehirler</p>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {popularItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleItemClick(item.id, item.name)}
                className="w-full px-4 py-3 flex items-center gap-3 hover:bg-[#2d3039] transition-colors"
              >
                <MapPin className="w-5 h-5 text-yellow-400" />
                <span className="text-white">{item.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}